/**
 * Headless SVG serialiser.
 *
 * Builds the file from the `FlowGraph` rather than cloning the live DOM, using
 * the same `shapeGeometry` / `palette` primitives the React renderer uses. That
 * shared origin is what makes an export match the screen, and it means export
 * works with no document at all — in a worker, in a test, on a server.
 *
 * Two hard rules:
 *  - **Every piece of user text is XML-escaped.** A node label is arbitrary
 *    input; unescaped, `</text><script>` would turn a diagram into a delivery
 *    vehicle. `escapeXml` is applied at every single text insertion point.
 *  - **The file is self-contained.** No scripts, no `<foreignObject>`, no
 *    external references, and every `var(--fs-…)` resolved to a literal colour,
 *    because a `var()` that resolves to nothing renders black.
 */
import type { FlowGraph, GraphEdge, GraphNode, Rect } from '../graph/model/types';
import { polylineToPath } from '../graph/routing/polyline';
import { applyColorOverride, colorsForRole, edgeColor } from '../diagram/palette';
import { arrowHead, shapeGeometry, terminalDirection, trimForArrow } from '../diagram/shapes';
import { iconPaths, NODE_ICON_VIEWBOX } from '../diagram/nodeIcons';
import { BRANDING } from '../config/branding';

export type ResolvedColors = Readonly<Record<string, string>>;

export interface SvgExportOptions {
  readonly padding: number;
  /** `null` for a transparent background. */
  readonly background: string | null;
  readonly scale: number;
  readonly region: 'whole' | Rect;
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly lineHeight: number;
  readonly cornerRadius: number;
  readonly title: string;
  readonly description: string;
  readonly colors: ResolvedColors;
  /** Restricts the export to these node ids (and the edges between them). */
  readonly nodeIds: readonly string[] | null;
}

/**
 * Every custom property the exporter reads. Kept as one list so the fallback
 * table below and `resolveThemeColors` cannot drift apart.
 */
export const EXPORT_COLOR_VARIABLES: readonly string[] = [
  '--fs-node-start-fill',
  '--fs-node-start-stroke',
  '--fs-node-start-text',
  '--fs-node-end-fill',
  '--fs-node-end-stroke',
  '--fs-node-end-text',
  '--fs-node-action-fill',
  '--fs-node-action-stroke',
  '--fs-node-action-text',
  '--fs-node-decision-fill',
  '--fs-node-decision-stroke',
  '--fs-node-decision-text',
  '--fs-node-loop-fill',
  '--fs-node-loop-stroke',
  '--fs-node-loop-text',
  '--fs-node-call-fill',
  '--fs-node-call-stroke',
  '--fs-node-call-text',
  '--fs-node-io-fill',
  '--fs-node-io-stroke',
  '--fs-node-io-text',
  '--fs-node-note-fill',
  '--fs-node-note-stroke',
  '--fs-node-note-text',
  '--fs-node-error-fill',
  '--fs-node-error-stroke',
  '--fs-node-error-text',
  '--fs-node-return-fill',
  '--fs-node-return-stroke',
  '--fs-node-return-text',
  '--fs-edge',
  '--fs-edge-back',
  '--fs-edge-label',
  '--fs-edge-label-bg',
  '--fs-group-stroke',
  '--fs-group-fill',
  '--fs-group-label',
  '--fs-diagram-bg',
];

/** Light-theme values, used when nothing better is available. */
const FALLBACK_COLORS: ResolvedColors = {
  '--fs-node-start-fill': '#0f766e',
  '--fs-node-start-stroke': '#0b5750',
  '--fs-node-start-text': '#ffffff',
  '--fs-node-end-fill': '#334155',
  '--fs-node-end-stroke': '#1e293b',
  '--fs-node-end-text': '#ffffff',
  '--fs-node-action-fill': '#ffffff',
  '--fs-node-action-stroke': '#94a3b8',
  '--fs-node-action-text': '#16181d',
  '--fs-node-decision-fill': '#fff7ed',
  '--fs-node-decision-stroke': '#ea882a',
  '--fs-node-decision-text': '#7c3f06',
  '--fs-node-loop-fill': '#f5f3ff',
  '--fs-node-loop-stroke': '#7c5cd6',
  '--fs-node-loop-text': '#3f2d78',
  '--fs-node-call-fill': '#eff6ff',
  '--fs-node-call-stroke': '#2563eb',
  '--fs-node-call-text': '#14306e',
  '--fs-node-io-fill': '#ecfeff',
  '--fs-node-io-stroke': '#0891b2',
  '--fs-node-io-text': '#0b4a56',
  '--fs-node-note-fill': '#fefce8',
  '--fs-node-note-stroke': '#d4b90a',
  '--fs-node-note-text': '#4a3f05',
  '--fs-node-error-fill': '#fdecea',
  '--fs-node-error-stroke': '#c0392b',
  '--fs-node-error-text': '#7a1f16',
  '--fs-node-return-fill': '#f1f5f9',
  '--fs-node-return-stroke': '#64748b',
  '--fs-node-return-text': '#1e293b',
  '--fs-edge': '#6b7280',
  '--fs-edge-back': '#7c5cd6',
  '--fs-edge-label': '#374151',
  '--fs-edge-label-bg': '#f4f5f7',
  '--fs-group-stroke': '#94a3b8',
  '--fs-group-fill': '#ffffff',
  '--fs-group-label': '#5a6270',
  '--fs-diagram-bg': '#f4f5f7',
};

export const DEFAULT_SVG_EXPORT_OPTIONS: SvgExportOptions = {
  padding: 24,
  background: null,
  scale: 1,
  region: 'whole',
  fontFamily:
    "'Inter var', Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  fontSize: 14,
  lineHeight: 19,
  cornerRadius: 6,
  title: `${BRANDING.name} diagram`,
  description: '',
  colors: FALLBACK_COLORS,
  nodeIds: null,
};

/** Reads the live theme so an export matches what the user is looking at. */
export function resolveThemeColors(root?: Element): ResolvedColors {
  if (typeof globalThis.getComputedStyle !== 'function') return FALLBACK_COLORS;
  const element = root ?? globalThis.document?.documentElement;
  if (!element) return FALLBACK_COLORS;

  const computed = globalThis.getComputedStyle(element);
  const out: Record<string, string> = { ...FALLBACK_COLORS };
  for (const name of EXPORT_COLOR_VARIABLES) {
    const value = computed.getPropertyValue(name).trim();
    if (value) out[name] = value;
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Escaping
 * ------------------------------------------------------------------ */

const XML_ESCAPES: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&apos;',
};

/** Escapes text for both element content and attribute values. */
export function escapeXml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => XML_ESCAPES[char] ?? char);
}

/** Turns `var(--fs-x)` into a literal colour; passes anything else through. */
export function resolveVar(value: string, colors: ResolvedColors): string {
  const match = /^var\((--[\w-]+)\)$/.exec(value.trim());
  if (!match) return value;
  const name = match[1]!;
  return colors[name] ?? FALLBACK_COLORS[name] ?? '#000000';
}

/* ------------------------------------------------------------------ *
 * Serialising
 * ------------------------------------------------------------------ */

export function exportSvg(graph: FlowGraph, options: Partial<SvgExportOptions> = {}): string {
  const opts: SvgExportOptions = { ...DEFAULT_SVG_EXPORT_OPTIONS, ...options };
  const colors = { ...FALLBACK_COLORS, ...opts.colors };
  const c = (value: string): string => escapeXml(resolveVar(value, colors));

  const selection = opts.nodeIds ? new Set(opts.nodeIds) : null;
  const nodes = selection ? graph.nodes.filter((n) => selection.has(n.id)) : graph.nodes;
  const nodeIds = new Set(nodes.map((n) => n.id));
  const edges = graph.edges.filter(
    (edge) => edge.points.length >= 2 && nodeIds.has(edge.source) && nodeIds.has(edge.target),
  );
  const groups = graph.groups.filter(
    (group) => group.bounds !== null && group.nodeIds.some((id) => nodeIds.has(id)),
  );

  const region = opts.region === 'whole' ? contentRect(nodes, edges, opts.padding) : opts.region;
  const width = Math.max(1, Math.round(region.width * opts.scale));
  const height = Math.max(1, Math.round(region.height * opts.scale));

  const body: string[] = [];

  if (opts.background !== null) {
    body.push(
      `<rect x="${num(region.x)}" y="${num(region.y)}" width="${num(region.width)}" height="${num(region.height)}" fill="${c(opts.background)}"/>`,
    );
  }

  for (const group of groups) body.push(...renderGroup(group.bounds!, group.label, c));
  for (const edge of edges) body.push(...renderEdge(edge, opts, c));
  for (const node of nodes) body.push(...renderNode(node, opts, c));

  const desc = opts.description || `${nodes.length} nodes, ${edges.length} connections.`;

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" role="img"`,
    ` width="${width}" height="${height}"`,
    ` viewBox="${num(region.x)} ${num(region.y)} ${num(region.width)} ${num(region.height)}">`,
    `<title>${escapeXml(opts.title)}</title>`,
    `<desc>${escapeXml(desc)}</desc>`,
    `<style>text{font-family:${escapeXml(opts.fontFamily)};white-space:pre}</style>`,
    ...body,
    '</svg>',
    '',
  ].join('');
}

function contentRect(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
  padding: number,
): Rect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of nodes) {
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + node.size.width);
    maxY = Math.max(maxY, node.position.y + node.size.height);
  }
  for (const edge of edges) {
    for (const point of edge.points) {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }
  }

  if (!Number.isFinite(minX)) return { x: 0, y: 0, width: 1, height: 1 };
  return {
    x: minX - padding,
    y: minY - padding,
    width: maxX - minX + padding * 2,
    height: maxY - minY + padding * 2,
  };
}

function renderGroup(bounds: Rect, label: string, c: (v: string) => string): string[] {
  return [
    `<rect x="${num(bounds.x)}" y="${num(bounds.y)}" width="${num(bounds.width)}" height="${num(bounds.height)}"`,
    ` rx="12" fill="${c('var(--fs-group-fill)')}" fill-opacity="0.35"`,
    ` stroke="${c('var(--fs-group-stroke)')}" stroke-width="1.25" stroke-dasharray="6 5"/>`,
    `<text x="${num(bounds.x + 12)}" y="${num(bounds.y + 15)}" font-size="11" font-weight="600"`,
    ` fill="${c('var(--fs-group-label)')}">${escapeXml(label)}</text>`,
  ];
}

function renderEdge(edge: GraphEdge, opts: SvgExportOptions, c: (v: string) => string): string[] {
  const colour = c(edgeColor(edge.isBackEdge));
  const trimmed = trimForArrow(edge.points, 7);
  const path = polylineToPath(trimmed, opts.cornerRadius);
  const head = arrowHead(edge.points[edge.points.length - 1]!, terminalDirection(edge.points), 8);

  const out = [
    `<path d="${escapeXml(path)}" fill="none" stroke="${colour}" stroke-width="1.5"`,
    ` stroke-linecap="round" stroke-linejoin="round"`,
    edge.isBackEdge ? ' stroke-dasharray="7 5"' : '',
    '/>',
    `<path d="${escapeXml(head)}" fill="${colour}"/>`,
  ];

  if (edge.label && edge.labelPosition) {
    const width = edge.label.length * opts.fontSize * 0.58 + 10;
    const height = opts.fontSize + 6;
    const { x, y } = edge.labelPosition;
    out.push(
      `<rect x="${num(x - width / 2)}" y="${num(y - height / 2)}" width="${num(width)}" height="${num(height)}"`,
      ` rx="4" fill="${c('var(--fs-edge-label-bg)')}" fill-opacity="0.92"/>`,
      `<text x="${num(x)}" y="${num(y + opts.fontSize * 0.35)}" text-anchor="middle"`,
      ` font-size="${opts.fontSize - 2}" font-weight="550" fill="${c('var(--fs-edge-label)')}">`,
      escapeXml(edge.label),
      '</text>',
    );
  }

  return out;
}

function renderNode(node: GraphNode, opts: SvgExportOptions, c: (v: string) => string): string[] {
  const geometry = shapeGeometry(node.shape, node.size);
  const palette = applyColorOverride(colorsForRole(node.role), node.metadata.color);
  const { x, y } = node.position;

  if (node.shape === 'junction') {
    return [
      `<g transform="translate(${num(x)} ${num(y)})">`,
      `<path d="${escapeXml(geometry.outline)}" fill="${c('var(--fs-edge)')}" fill-opacity="0.55"/>`,
      '</g>',
    ];
  }

  const icons = iconPaths(node.metadata.icon);
  const hasIcon = icons.length > 0;
  const iconSize = 18;
  const iconGap = hasIcon ? iconSize + 6 : 0;
  const textLeft = geometry.textBox.x + iconGap;
  const textWidth = Math.max(0, geometry.textBox.width - iconGap);
  const centreX = textLeft + textWidth / 2;
  const blockHeight = node.lines.length * opts.lineHeight;
  const firstBaseline =
    geometry.textBox.y + (geometry.textBox.height - blockHeight) / 2 + opts.fontSize * 0.82;

  const out: string[] = [
    `<g transform="translate(${num(x)} ${num(y)})">`,
    `<path d="${escapeXml(geometry.outline)}" fill="${escapeXml(resolveVar(palette.fill, opts.colors))}"`,
    ` stroke="${escapeXml(resolveVar(palette.stroke, opts.colors))}" stroke-width="1.5" stroke-linejoin="round"`,
    node.role === 'error' ? ' stroke-dasharray="5 4"' : '',
    '/>',
  ];

  for (const detail of geometry.details) {
    out.push(
      `<path d="${escapeXml(detail)}" fill="none" stroke="${escapeXml(resolveVar(palette.stroke, opts.colors))}"`,
      ' stroke-width="1.25" stroke-opacity="0.8"/>',
    );
  }

  if (hasIcon) {
    const scale = iconSize / NODE_ICON_VIEWBOX;
    const iconY = geometry.textBox.y + geometry.textBox.height / 2 - iconSize / 2;
    out.push(
      `<g transform="translate(${num(geometry.textBox.x)} ${num(iconY)}) scale(${num(scale)})"`,
      ` fill="none" stroke="${escapeXml(resolveVar(palette.text, opts.colors))}" stroke-width="1.8"`,
      ' stroke-linecap="round" stroke-linejoin="round" stroke-opacity="0.85">',
      ...icons.map((d) => `<path d="${escapeXml(d)}"/>`),
      '</g>',
    );
  }

  const weight = node.role === 'start' || node.role === 'end' ? 600 : 450;
  out.push(
    `<text x="${num(centreX)}" y="${num(firstBaseline)}" text-anchor="middle"`,
    ` font-size="${opts.fontSize}" font-weight="${weight}"`,
    ` fill="${escapeXml(resolveVar(palette.text, opts.colors))}">`,
    ...node.lines.map(
      (line, index) =>
        `<tspan x="${num(centreX)}"${index === 0 ? '' : ` dy="${opts.lineHeight}"`}>${escapeXml(line)}</tspan>`,
    ),
    '</text>',
    '</g>',
  );

  return out;
}

/** Fixed-precision numbers, so output is byte-identical across runs. */
function num(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Object.is(rounded, -0) ? '0' : String(rounded);
}
